import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Table, Pagination } from '@treezor/design-system';

import { EmptyState } from '@/ui-kit/templates/EmptyState/EmptyState';

import { TOnboardingUserType } from '@/store/models/onboarding-user.model';

import { fetchOnboardingUsers } from '@/store/actions/onboarding-user.action';

import { TableSkeleton } from '@/ui-kit/loaders/TableSkeleton';

import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';
import { useCursor } from '@/hooks/useCursor';

import { useOnboardingUserTable } from './hooks/useOnboardingUserTable';

interface IOnboardingUsersTabProps {
  type: TOnboardingUserType;
}

export const PER_PAGE_USERS = 24;

export const OnboardingUsersTab = ({ type }: IOnboardingUsersTabProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const isLoading = useAppSelector((state) => state.onboardingUser[type].isLoading);

  const { elements = [], cursor } = useAppSelector((state) => state.onboardingUser[type].state);

  const { headers, rows } = useOnboardingUserTable({ type, onboardingUsers: elements });

  const handlePagination = useCallback(
    (cursorId) => {
      dispatch(fetchOnboardingUsers({ type, cursor: cursorId }));
    },
    [dispatch, type],
  );

  const pagination = useCursor(cursor, PER_PAGE_USERS, handlePagination);

  return (
    <div className="mb-5 bg-primary">
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <Table
          columns={headers}
          rows={rows}
          emptyComponent={
            <EmptyState title={t('components.molecules.table.noRecord')} icon="search_v1" size="m" className="p-6" />
          }
        />
      )}
      {(!!cursor?.next || !!cursor?.prev) && (
        <Pagination
          {...pagination}
          isFirstButtonVisible
          isLastButtonVisible={false}
          isPagesVisible={false}
          className="flex justify-end w-full"
          isDisabled={false}
        />
      )}
    </div>
  );
};
